import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Briefcase,
  FileText,
  LogOut,
  Menu,
  User,
  Users,
  X,
} from "lucide-react";
import { health, type HealthOut } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spark } from "../components/Spark";
import { Wordmark } from "../components/Wordmark";

/*
 * Shell for the "Your record" tools (profile, publications, recommenders,
 * applications, attorneys). Design system v2: warm `soft` background, a slim
 * top bar that matches the Case Plan, and one compact row of secondary nav.
 *
 * The Case Plan (/scholar-path) owns progress. This shell deliberately shows
 * no progress meter of its own so the two never disagree.
 */

type NavItem = {
  to: string;
  label: string;
  icon: typeof User;
  /** Extra path prefixes that should light this item up. */
  also?: string[];
};

const NAV: NavItem[] = [
  { to: "/profile", label: "Profile", icon: User, also: ["/onboarding"] },
  { to: "/publications", label: "Publications", icon: BookOpen, also: ["/intake"] },
  { to: "/recommenders", label: "Recommenders", icon: Users },
  { to: "/applications", label: "Applications", icon: FileText },
  { to: "/marketplace", label: "Attorneys", icon: Briefcase, also: ["/engagements"] },
];

function isActive(item: NavItem, pathname: string) {
  return [item.to, ...(item.also ?? [])].some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

export default function AppShell() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [healthInfo, setHealthInfo] = useState<HealthOut | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    health.get().then(setHealthInfo).catch(() => undefined);
  }, []);

  // Close menus on navigation.
  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!accountOpen) return;
    function onDown(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setAccountOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [accountOpen]);

  const current = NAV.find((n) => isActive(n, pathname));
  const email = session?.userEmail ?? "";
  const initial = email[0]?.toUpperCase() ?? "?";

  function handleSignOut() {
    signOut();
    navigate("/sign-in");
  }

  return (
    <div className="app-v2 flex min-h-screen flex-col bg-soft text-ink">
      <header className="sticky top-0 z-30 border-b border-hair bg-soft/90 backdrop-blur">
        {/* Top bar — mirrors the Case Plan header */}
        <div className="mx-auto flex h-14 max-w-[1120px] items-center justify-between gap-3 px-4 sm:px-6">
          <Link
            to="/scholar-path"
            className="flex min-w-0 items-center gap-2.5 rounded-lg"
            aria-label="VisaGenius — back to your Case Plan"
          >
            <Spark size={32} tile="forest" />
            <Wordmark size={19} />
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/scholar-path"
              className="hidden h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-surface px-3.5 text-small font-semibold text-ink-1 transition-colors hover:border-[color:var(--ring-muted)] hover:text-ink sm:inline-flex"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Case Plan
            </Link>

            <div className="relative" ref={accountRef}>
              <button
                type="button"
                onClick={() => setAccountOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                aria-label="Account"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-surface text-small font-semibold text-ink transition-colors hover:border-[color:var(--ring-muted)]"
              >
                {initial}
              </button>
              {accountOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-40 mt-2 w-64 overflow-hidden rounded-2xl border border-line bg-surface shadow-raised"
                >
                  <div className="border-b border-hair px-4 py-3">
                    <p className="truncate text-small font-semibold text-ink">
                      {session?.tenantDisplayName || email.split("@")[0]}
                    </p>
                    <p className="truncate text-caption text-ink-3">{email}</p>
                  </div>
                  <Link
                    role="menuitem"
                    to="/scholar-path"
                    className="flex items-center gap-2 px-4 py-2.5 text-small text-ink-1 hover:bg-soft sm:hidden"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    Back to Case Plan
                  </Link>
                  <button
                    role="menuitem"
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-small text-ink-1 hover:bg-soft"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Secondary nav — "Your record" */}
        <div className="border-t border-hair">
          <div className="mx-auto max-w-[1120px] px-4 sm:px-6">
            {/* ≥ 768px: inline tabs */}
            <nav aria-label="Your record" className="hidden h-12 items-center gap-1 md:flex">
              <span className="eyebrow mr-3">Your record</span>
              {NAV.map((item) => {
                const active = isActive(item, pathname);
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    aria-current={active ? "page" : undefined}
                    className={`relative inline-flex h-12 items-center gap-1.5 whitespace-nowrap px-3 text-small font-medium transition-colors ${
                      active ? "text-ink" : "text-ink-2 hover:text-ink"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${active ? "text-terra-600" : "text-ink-3"}`} aria-hidden />
                    {item.label}
                    {active && (
                      <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-terra-500" aria-hidden />
                    )}
                  </NavLink>
                );
              })}
            </nav>

            {/* < 768px: one-line menu button */}
            <div className="md:hidden">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-controls="record-menu"
                className="flex h-12 w-full items-center justify-between gap-2 text-left"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="eyebrow">Your record</span>
                  <span className="text-ink-3" aria-hidden>·</span>
                  <span className="truncate text-small font-semibold text-ink">
                    {current?.label ?? "Menu"}
                  </span>
                </span>
                {menuOpen ? (
                  <X className="h-5 w-5 shrink-0 text-ink-2" aria-hidden />
                ) : (
                  <Menu className="h-5 w-5 shrink-0 text-ink-2" aria-hidden />
                )}
              </button>
              {menuOpen && (
                <nav id="record-menu" aria-label="Your record" className="pb-3">
                  <ul className="overflow-hidden rounded-2xl border border-line bg-surface">
                    {NAV.map((item) => {
                      const active = isActive(item, pathname);
                      const Icon = item.icon;
                      return (
                        <li key={item.to} className="border-b border-hair last:border-b-0">
                          <NavLink
                            to={item.to}
                            aria-current={active ? "page" : undefined}
                            className={`flex h-12 items-center gap-3 px-4 text-body ${
                              active ? "bg-terra-50 font-semibold text-terra-700" : "text-ink-1"
                            }`}
                          >
                            <Icon className="h-4 w-4" aria-hidden />
                            {item.label}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </header>

      {healthInfo?.demo_mode && (
        <div className="border-b border-warn bg-amber-50">
          <p className="mx-auto max-w-[1120px] px-4 py-2 text-small text-amber-600 sm:px-6">
            <strong className="font-semibold">Demo mode</strong> — AI drafting is off, so
            generated text is placeholder.
            <span className="ml-2 text-caption text-amber-600/80">{healthInfo.environment}</span>
          </p>
        </div>
      )}

      <main className="flex flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}
